import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import ChapterHeading from "@/components/landing/ChapterHeading";
import api, { API } from "@/lib/api";

const PHOTOS = [
  { src: "/photos/gallery/cortile.webp", cat: "esterni", label: "La casa vista dal giardino", wide: true },
  { src: "/photos/gallery/prato.webp", cat: "esterni", label: "Il cortile tra le mura di pietra" },
  { src: "/photos/gallery/capitolo1.webp", cat: "esterni", label: "L'amaca sul prato" },
  { src: "/photos/gallery/giardino.webp", cat: "esterni", label: "Sedie e amaca nel verde", wide: true },
  { src: "/photos/gallery/amaca.webp", cat: "esterni", label: "Il giardino dall'alto" },
  { src: "/photos/gallery/giardino_alto.webp", cat: "esterni", label: "Il prato tra i muri" },
  { src: "/photos/gallery/esterno_sera.jpg", cat: "esterni", label: "Il vicolo del borgo" },
];

export default function Gallery() {
  const [open, setOpen] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    api.get("/photos").then((res) => setUploaded(res.data.photos || [])).catch(() => {});
  }, []);

  const photos = [
    ...uploaded.map((p) => ({ src: `${API}/photos/${p.id}/image`, label: p.label || "Foto dell'host" })),
    ...PHOTOS,
  ];

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % photos.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, photos.length]);

  return (
    <section id="galleria" data-testid="gallery-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
      <ChapterHeading
        eyebrow="La Galleria"
        title="Il giardino e gli spazi esterni"
        subtitle="Il cortile, il prato e l'amaca tra le mura secolari — le foto vere degli spazi all'aperto. Tocca una foto per vederla a tutto schermo."
      />

      <motion.div layout className="grid auto-rows-[160px] grid-cols-2 gap-3 sm:auto-rows-[200px] md:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {photos.map((p, i) => (
            <motion.button
              layout
              key={p.src}
              type="button"
              data-testid={`gallery-photo-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35 }}
              onClick={() => setOpen(i)}
              className={`group relative overflow-hidden rounded-sm ${p.wide ? "col-span-2" : ""}`}
            >
              <img
                src={p.src}
                alt={p.label}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              />
              <span className="absolute inset-0 bg-stone-950/0 transition-colors duration-300 group-hover:bg-stone-950/30" />
              <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-linen/90 text-stone-800 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <Expand size={14} />
              </span>
              <span className="absolute bottom-2 left-2 max-w-[80%] truncate rounded-full bg-stone-950/60 px-3 py-1 text-[11px] text-linen opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                {p.label}
              </span>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {open !== null && photos[open] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex flex-col bg-stone-950/95 backdrop-blur-sm"
            onClick={() => setOpen(null)}
          >
            <div className="flex items-center justify-between px-5 py-4 text-linen">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-linen/60" data-testid="gallery-counter">
                {open + 1} / {photos.length}
              </p>
              <button
                data-testid="gallery-close-btn"
                onClick={() => setOpen(null)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-linen/25 transition-colors hover:bg-linen/10"
                aria-label="Chiudi"
              >
                <X size={18} />
              </button>
            </div>

            <div className="relative flex flex-1 items-center justify-center px-4 pb-4" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="gallery-prev-btn"
                onClick={() => setOpen((open - 1 + photos.length) % photos.length)}
                className="absolute left-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-linen/25 text-linen transition-colors hover:bg-linen/10 sm:left-8"
                aria-label="Foto precedente"
              >
                <ChevronLeft size={20} />
              </button>

              <AnimatePresence mode="wait">
                <motion.figure
                  key={photos[open].src}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.35 }}
                  className="flex max-h-full flex-col items-center"
                >
                  <img
                    src={photos[open].src}
                    alt={photos[open].label}
                    className="max-h-[76vh] w-auto max-w-full rounded-sm object-contain shadow-2xl"
                  />
                  <figcaption className="mt-4 text-center font-serif text-lg italic text-linen/85">
                    {photos[open].label}
                  </figcaption>
                </motion.figure>
              </AnimatePresence>

              <button
                data-testid="gallery-next-btn"
                onClick={() => setOpen((open + 1) % photos.length)}
                className="absolute right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-linen/25 text-linen transition-colors hover:bg-linen/10 sm:right-8"
                aria-label="Foto successiva"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
